import useSlideOver from '../../../useSlideOver';
import useModal from '../../../../modal/useModal';
import UseContext from '../../../../../hooks/useContext';
import SlideOverHeader from '../../../component/header';
import SlideOverBody from '../../../component/body';
import SlideOverFooter from '../../../component/footer';
import ApiDatabase from 'server';
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  RotateFadingClock,
} from '../../../../../assets/icons';
import Table from '../../../../table/table';
import Banner from '../../../../banner/banner';
import Button from '../../../../button';
import { toast } from 'react-toastify';
import CopyToClipboard from '../../../../copyToClipboard';
import validateCoefficientRanges from 'helper/validateCoefficientRanges';
import { isDefaultCoefficientRanges } from 'helper/coefficientRange';
import { formatLastUpdate, formatRelativeDay } from 'helper/lastUpdate';
import SlideOverCompanyOverview from './slideOverCompanyOverview';
import SlideOverCompanyContactsTab from './SlideOverCompanyContactsTab';
import SlideOverCompanyRemunerationConditions from './slideOverCompanyRemunerationConditions';
import SlideOverCompanyInvoicesTab from './SlideOverCompanyInvoicesTab';
import SlideOverCompanyMissions from './slideOverCompanyMissions';
import SlideOverCompanyActivity from './slideOverCompanyActivity';
import CompanyContactEditView from './CompanyContactEditView';
import useCompanyPerimeters from './useCompanyPerimeters';

const SlideOverCompany = () => {
  const { removeAllParameters } = UseContext();
  const {
    objID,
    target,
    userData,
    modalDataReturn,
    setModalData,
    setModalDataReturn,
    refreshCompanyBigAccount,
    language,
  } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const [companyInfo, setCompanyInfo] = useState({});
  const token = localStorage.getItem('xsrfToken');
  const [isShowCoeff, setIsShowCoeff] = useState(false);
  // The contact being edited: its screen replaces the panel, like the coefficients one.
  const [editedMemberId, setEditedMemberId] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  // The sector and the big account are fetched from here, beside the company,
  // so the three requests leave together — under the overview they could only
  // start once the panel had replaced its skeleton.
  const [pennylaneCategoryList, setPennylaneCategoryList] = useState([]);
  const [bigAccount, setBigAccount] = useState('');
  const [isCategoriesWaiting, setIsCategoriesWaiting] = useState(true);
  const [isBigAccountWaiting, setIsBigAccountWaiting] = useState(true);
  const [pageType, setPageType] = useState(
    target === 'invoices'
      ? 'invoices'
      : target === 'remuneration_conditions'
        ? 'remuneration_conditions'
        : target === 'members'
          ? 'members'
          : 'overview'
  );
  // Read here rather than in the contacts tab: the contact screen replaces
  // the tab and shows the member's perimeters from the same lists. Only read
  // while the tab (or its contact screen) is on.
  const perimeters = useCompanyPerimeters(
    pageType === 'members' ? companyInfo?._id : undefined
  );
  const [isCoeffSend, setIsCoeffSend] = useState(false);
  // The write is fired from the confirmation modal, not from the button below,
  // so without this the panel gave no sign anything was happening — and a
  // second click sent the ranges twice.
  const [isCoeffSaving, setIsCoeffSaving] = useState(false);
  const scrollContainerRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const titlesCoeff = [
    'TRANCHES DE SALAIRE BRUT MENSUEL',
    'COEFF. DÉLÉGATION',
    'COEFF. GESTION',
  ];

  // Local state, lifted here instead of the shared context: adding and
  // removing rows does not work well through state shared with other pages.
  const [coeffRows, setCoeffRows] = useState([]);
  const [coeffErrors, setCoeffErrors] = useState([]);
  const [coeffBanner, setCoeffBanner] = useState(null);
  // Only validate live once something has been edited: on open the panel
  // reports what is stored, it does not accuse the user of a mistake they
  // have not made yet.
  const [coeffTouched, setCoeffTouched] = useState(false);
  // The attestation lives in the confirmation modal since JBWEB-1214, beside
  // the document it now attests to. It reaches the save from there.

  useEffect(() => {
    setCoeffRows(companyInfo?.coefficients ?? []);
    setCoeffErrors([]);
    setCoeffBanner(null);
    setCoeffTouched(false);
  }, [companyInfo?.coefficients]);

  // Live validation, so the banner does not wait for Confirmer. One debounce
  // covers both cases: leaving a field commits its value to coeffRows, and
  // typing in one commits on each keystroke — either way the check runs 300 ms
  // after the last change. It also clears the red as soon as a field becomes
  // valid again, which JBWEB-1074 asks for.
  const markCoeffTouched = (updater) => {
    setCoeffTouched(true);
    setCoeffRows(updater);
  };

  // Validated on open, not only after an edit: the SMIC rises while stored
  // grids stay put, so a company can fall under it without anyone touching the
  // panel. The server refuses those same grids on save, so showing the alert
  // here only makes an existing refusal visible earlier.
  useEffect(() => {
    const timer = setTimeout(() => {
      const errors = validateCoefficientRanges(coeffRows);
      setCoeffErrors(errors);
      setCoeffBanner(errors.length > 0 ? errors[0].message : null);
    }, 300);
    return () => clearTimeout(timer);
  }, [coeffRows]);

  useEffect(() => {
    if (target === 'invoices') {
      setPageType('invoices');
    } else if (target === 'remuneration_conditions') {
      setPageType('remuneration_conditions');
    } else if (target === 'members') {
      setPageType('members');
    } else {
      setPageType('overview');
    }
  }, [target, objID]);

  const fetchCompanyInfo = (showLoader = true) => {
    if (!objID) return;
    if (showLoader) setIsWaiting(true);
    ApiDatabase.getCompanyInfo(
      { token, idCompany: objID },
      (data) => {
        setCompanyInfo(data.company);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaiting(false);
        }
      }
    );
  };

  useEffect(() => {
    setEditedMemberId(null);
    fetchCompanyInfo();
  }, [objID]);

  const loadPennylaneCategories = () => {
    setIsCategoriesWaiting(true);
    ApiDatabase.getPennyLaneCategories(
      { token },
      (data) => {
        setPennylaneCategoryList(data?.items || []);
        setIsCategoriesWaiting(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        setIsCategoriesWaiting(false);
      }
    );
  };

  useEffect(() => {
    loadPennylaneCategories();
  }, []);

  useEffect(() => {
    if (!objID) return;
    setIsBigAccountWaiting(true);
    ApiDatabase.getCompanyBigAccount(
      { token, idCompany: objID },
      (data) => {
        setBigAccount(data?.bigAccount ?? '');
        setIsBigAccountWaiting(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        setIsBigAccountWaiting(false);
      }
    );
  }, [objID, refreshCompanyBigAccount]);

  useEffect(() => {
    if (!modalDataReturn) return;
    if (modalDataReturn?.action === 'invoices_deleted' && modalDataReturn.invoice_numbers) {
      setCompanyInfo((prev) => {
        if (!prev?.factures) return prev;
        return {
          ...prev,
          factures: prev.factures.filter(
            (f) => !modalDataReturn.invoice_numbers.includes(f.external_id)
          ),
        };
      });
    } else if (modalDataReturn?.action === 'invoices_imported' && modalDataReturn.invoice_numbers) {
      setCompanyInfo((prev) => {
        if (!prev?.factures) return prev;
        return {
          ...prev,
          factures: prev.factures.map((f) =>
            modalDataReturn.invoice_numbers.includes(f.external_id)
              ? { ...f, payment_status: f.payment_status || 'pending' }
              : f
          ),
        };
      });
    }
  }, [modalDataReturn]);

  const closeSlideOver = () => {
    toggleSlideOver();
    removeAllParameters();
  };

  const checkScrollable = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setIsScrollable(scrollLeft > 0 || scrollLeft + clientWidth < scrollWidth);
    }
  };

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, []);

  const handleScroll = () => checkScrollable();

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // "Confirmer" validates first: the modal only opens once everything
  // passes, and nothing is persisted until it has been confirmed.
  // The back replays the same rules on save (values end up as billing
  // rates and the front can be bypassed), so this front-side copy is a
  // UX shortcut, not the source of truth.
  // Confirmer needs three things: something to write, the attestation covering
  // it, and a grid without alerts — an unresolved one blocks the save here and
  // again on the server.
  const canSubmitCoeff = coeffTouched && coeffErrors.length === 0;

  const submitCoeff = () => {
    if (!canSubmitCoeff) return;
    const errors = validateCoefficientRanges(coeffRows);
    if (errors.length > 0) {
      setCoeffErrors(errors);
      setCoeffBanner(errors[0].message);
      return;
    }
    setCoeffErrors([]);
    setCoeffBanner(null);
    setModalData({ coefficients: coeffRows });
    toggle('confirm_payment_settings');
  };

  const persistCoeff = (coefficients, attested, justificatif) => {
    setIsCoeffSaving(true);
    ApiDatabase.postCoefficient(
      { token, idCompany: objID, coefficients, attested, justificatif },
      () => {
        setIsCoeffSaving(false);
        setIsCoeffSend(true);
        setCoeffErrors([]);
        setCoeffBanner(null);
            toast.success('Les coefficients ont bien été enregistrés');
        fetchCompanyInfo(false);
      },
      (err) => {
        setIsCoeffSaving(false);
        if (err.name === 'CanceledError') return;
        const payload = err?.response?.data;
        if (payload?.errors) {
          setCoeffErrors(payload.errors);
          setCoeffBanner(payload.errors[0].message);
        } else if (payload?.error) {
          // Rejections that belong to no single row — a missing attestation —
          // arrive as { error, errorCode } and only feed the banner.
          setCoeffBanner(payload.error);
        } else {
          console.error(err);
          toast.error("L'enregistrement a échoué");
        }
      }
    );
  };

  useEffect(() => {
    if (modalDataReturn?.type === 'payment_settings_confirmed') {
      // The modal cannot confirm without the box ticked, so reaching here is
      // the attestation. The API still refuses a save that arrives without it.
      persistCoeff(
        modalDataReturn.coefficients,
        modalDataReturn.attested,
        modalDataReturn.justificatif
      );
      setModalDataReturn(null);
    }
    // The Pennylane sector decides which default coefficient grid applies, and
    // the panel names that sector on two of its labels. Patching the id in
    // place would leave both stale, so the company is read back instead.
    if (modalDataReturn?.type === 'company_activity_updated') {
      fetchCompanyInfo(false);
      setModalDataReturn(null);
    }
  }, [modalDataReturn]);

  const restoreDefaults = () => {
    setCoeffTouched(true);
    setCoeffRows(
      (companyInfo?.defaultCoefficients ?? []).map((range) => ({ ...range }))
    );
    setCoeffErrors([]);
    setCoeffBanner(null);
  };

  // Leaving the panel unmounts it, but coeffRows lives here: without an
  // explicit reset, reopening would show the abandoned draft as if it had
  // been stored.
  const discardCoeffDraft = () => {
    setCoeffRows((companyInfo?.coefficients ?? []).map((range) => ({ ...range })));
    setCoeffErrors([]);
    setCoeffBanner(null);
    setCoeffTouched(false);
    setIsShowCoeff(false);
  };

  // The stored ranges still matching the template is what "default values"
  // means here — nothing records it. Kept as an additional reason to warn, on
  // top of the incomplete case isCoeffSend already covers.
  const isCoeffOnDefaults = isDefaultCoefficientRanges(
    companyInfo?.coefficients,
    companyInfo?.defaultCoefficients
  );

  // The API resolves the sector from the company's PennyLane category, and
  // leaves it null when the category maps to no grid. Naming it tells the
  // admin which grid the button is about to apply.
  const defaultCoefficientsSector = companyInfo?.defaultCoefficientsSector;

  // Nothing to show until the ranges have been saved once: coefficients cannot
  // be written without attesting, so a stored set is always an attested one.
  const coefficientsAttestation = companyInfo?.coefficientsAttestation;
  const attestationLabel = coefficientsAttestation?.at
    ? `Attesté ${formatRelativeDay(coefficientsAttestation.at)}${
        coefficientsAttestation.by
          ? ` par ${coefficientsAttestation.by.firstname} ${(
              coefficientsAttestation.by.lastname ?? ''
            ).toUpperCase()}`
          : ''
      }`
    : null;

  // Who last touched the grid, beside who attested it: the attestation says the
  // values were vouched for, this says when they last moved. Same two events the
  // Coefficient de facturation card reads on the page behind.
  const coefficientsLastUpdate = formatLastUpdate(
    companyInfo?.lastUpdates?.modification_coefficient_facturation ??
      companyInfo?.lastUpdates?.ajout_coefficient_facturation
  );

  const editedMember = (companyInfo?.employees ?? []).find(
    (entry) => entry.user?._id === editedMemberId
  );

  // keepOpen: the contact is saved but the screen still has work to show (its
  // perimeters failed), so only the row is patched.
  const applySavedMember = (saved, { keepOpen = false } = {}) => {
    setCompanyInfo((previous) => ({
      ...previous,
      employees: (previous.employees ?? []).map((entry) =>
        entry.user?._id === saved._id
          ? {
              ...entry,
              role: saved.role,
              user: {
                ...entry.user,
                firstname: saved.firstname,
                lastname: saved.lastname,
                email: saved.email,
                phone: saved.phone,
                validation: saved.validation,
              },
            }
          : entry
      ),
    }));
    if (!keepOpen) setEditedMemberId(null);
  };

  return (
    <>
      {isWaiting ? (
        <>
          <SlideOverHeader hide={closeSlideOver} hideShow={true}>
            <div>
              <div className="w-56 h-5 skeleton-bar"></div>
            </div>
          </SlideOverHeader>
          <SlideOverBody>
            <div className={'flex flex-col gap-2'}>
              <button
                type={'button'}
                className={'text-left border rounded p-3 mb-5'}
              >
                <div className={'flex justify-between'}>
                  <div className={'my-auto'}>
                    <div className="w-56 h-3 skeleton-bar"></div>
                  </div>
                  <div className={'my-auto'}>
                    <ChevronRight wh={24} color={'#111827'} />
                  </div>
                </div>
              </button>
              <div className={''}>
                <div
                  className={'border rounded p-3 flex justify-between gap-2'}
                >
                  <div className={'my-auto w-full'}>
                    <div className="w-56 h-3 skeleton-bar"></div>
                  </div>
                  <div className={'my-auto w-full'}>
                    <div className="w-56 h-6 skeleton-bar"></div>
                  </div>
                </div>
              </div>
              <div className={''}>
                <div
                  className={'border rounded p-3 flex justify-between gap-2'}
                >
                  <div className={'my-auto'}>
                    <div className="w-72 h-3 skeleton-bar"></div>
                  </div>
                </div>
              </div>
              <div className={'border rounded p-3 flex flex-col gap-1'}>
                <div className="w-56 h-3 skeleton-bar"></div>
                <div className="w-56 h-3 skeleton-bar"></div>
                <div className="w-36 h-3 skeleton-bar"></div>
              </div>
            </div>
          </SlideOverBody>
        </>
      ) : (
        <>
          {editedMember ? (
            <CompanyContactEditView
              companyInfo={companyInfo}
              member={editedMember}
              perimeters={perimeters}
              onBack={() => setEditedMemberId(null)}
              onSaved={applySavedMember}
            />
          ) : !isShowCoeff ? (
            <>
              <SlideOverHeader hide={closeSlideOver} hideShow={true}>
                <div className={'flex flex-col'}>
                  {companyInfo?.name_commercial}
                  <div className={'text-sm text-gray-500'}>
                    <CopyToClipboard text={companyInfo?.id_bestt}>
                      {companyInfo?.id_bestt}
                    </CopyToClipboard>
                  </div>
                </div>
              </SlideOverHeader>
              <SlideOverBody>
                <div
                  className={
                    'flex flex-col gap-5 text-sm h-full overflow-y-auto'
                  }
                >
                  <div className={'flex items-center gap-2 w-full'}>
                    {isScrollable && (
                      <Button size="ICON" color="ICON" onClick={scrollLeft}>
                        <ChevronLeft wh={24} color="#111827" />
                      </Button>
                    )}
                    <div
                      ref={scrollContainerRef}
                      className={'flex gap-2 overflow-x-auto no-scrollbar'}
                    >
                      <div
                        className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                          pageType === 'overview'
                            ? 'bg-gray-100'
                            : 'text-gray-500'
                        }`}
                        onClick={() => setPageType('overview')}
                      >
                        Vue d'ensemble
                      </div>
                      <div
                        className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                          pageType === 'members'
                            ? 'bg-gray-100'
                            : 'text-gray-500'
                        }`}
                        onClick={() => setPageType('members')}
                      >
                        {language.getValue(
                          'companyContacts.tabLabel',
                          'Contacts',
                        )}
                      </div>
                      <div
                        className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                          pageType === 'remuneration_conditions'
                            ? 'bg-gray-100'
                            : 'text-gray-500'
                        }`}
                        onClick={() => setPageType('remuneration_conditions')}
                      >
                        Conditions de rémunération
                      </div>
                      <div
                        className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                          pageType === 'missions'
                            ? 'bg-gray-100'
                            : 'text-gray-500'
                        }`}
                        onClick={() => setPageType('missions')}
                      >
                        Missions
                      </div>
                      {userData?.accessInvoice && (
                        <div
                          className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                            pageType === 'invoices'
                              ? 'bg-gray-100'
                              : 'text-gray-500'
                          }`}
                          onClick={() => setPageType('invoices')}
                        >
                          Factures
                        </div>
                      )}
                      <div
                        className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                          pageType === 'activity'
                            ? 'bg-gray-100'
                            : 'text-gray-500'
                        }`}
                        onClick={() => setPageType('activity')}
                      >
                        Activité
                      </div>
                    </div>
                    {isScrollable && (
                      <Button size="ICON" color="ICON" onClick={scrollRight}>
                        <ChevronRight wh={24} color="#111827" />
                      </Button>
                    )}
                  </div>
                  {/* The tab's own scroll container: flex-1, not h-full (under the
                      tab bar a full height overflows the slide-over body), and
                      relative so an absolute child (an sr-only label) stays inside
                      it instead of stretching the body, which would scroll too. */}
                  <div className={'relative flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto'}>
                    {pageType === 'overview' && (
                      <SlideOverCompanyOverview
                        companyInfo={companyInfo}
                        setCompanyInfo={setCompanyInfo}
                        pennylaneCategoryList={pennylaneCategoryList}
                        bigAccount={bigAccount}
                        isActivityWaiting={
                          isCategoriesWaiting || isBigAccountWaiting
                        }
                        onReloadPennylaneCategories={loadPennylaneCategories}
                      />
                    )}
                    {pageType === 'members' && (
                      <SlideOverCompanyContactsTab
                        companyInfo={companyInfo}
                        setCompanyInfo={setCompanyInfo}
                        onEditMember={setEditedMemberId}
                        perimeters={perimeters}
                      />
                    )}
                    {pageType === 'remuneration_conditions' && (
                      <SlideOverCompanyRemunerationConditions
                        companyInfo={companyInfo}
                        setCompanyInfo={setCompanyInfo}
                        setIsShowCoeff={setIsShowCoeff}
                        setIsCoeffSend={setIsCoeffSend}
                        onSaved={() => fetchCompanyInfo(false)}
                      />
                    )}
                    {pageType === 'missions' && (
                      <SlideOverCompanyMissions
                        companyInfo={companyInfo}
                      />
                    )}
                    {pageType === 'activity' && (
                      <SlideOverCompanyActivity companyInfo={companyInfo} />
                    )}
                    {/* Its tab shows with the invoice access only; a panel opened
                        on this tab without it shows nothing. Mounted with the
                        panel, hidden until its tab is chosen, so that its cards
                        have read their data by then; `contents` keeps them items
                        of this column, spaced like the other tabs' cards. */}
                    {userData?.accessInvoice && (
                      <div className={pageType === 'invoices' ? 'contents' : 'hidden'}>
                        <SlideOverCompanyInvoicesTab
                          companyInfo={companyInfo}
                          userData={userData}
                          isShown={pageType === 'invoices'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </SlideOverBody>
            </>
          ) : (
            <>
              <SlideOverHeader hide={closeSlideOver} hideShow={false}>
                <div className={'text-sm mb-3'}>
                  <button
                    type={'button'}
                    className={'border rounded p-2'}
                    onClick={discardCoeffDraft}
                  >
                    <div className={'flex justify-between gap-3'}>
                      <div className={'my-auto'}>
                        <ChevronLeft wh={20} color={'#111827'} />
                      </div>
                      <div className={'my-auto'}>Profil de l'entreprise</div>
                    </div>
                  </button>
                </div>
                <div>Paramètres de paiement</div>
                <div className={'text-gray-500 text-sm pt-0.5'}>
                  Consulter et gérer le coefficient de facuration et les
                  conditions de paiement de l’entreprise utilisatrice.
                </div>
                {/* Spelled out here rather than hidden in a tooltip like the
                    cards on the page behind: this header has the room, and the
                    grid is the one setting on the panel. font-normal because
                    the header is a semibold h3. */}
                {coefficientsLastUpdate ? (
                  <div
                    className={
                      'flex items-center gap-1.5 text-gray-500 text-sm font-normal pt-1'
                    }
                  >
                    <RotateFadingClock wh={16} color={'#6B7280'} />
                    <span>{coefficientsLastUpdate}</span>
                  </div>
                ) : null}
              </SlideOverHeader>
              <SlideOverBody>
                <div className={'flex flex-col gap-4 h-full'}>
                  {coeffBanner ? (
                    <Banner type="alert">{coeffBanner}</Banner>
                  ) : !isCoeffSend || isCoeffOnDefaults ? (
                    <Banner type="warning">
                      {defaultCoefficientsSector
                        ? `Coefficients par défaut du secteur ${defaultCoefficientsSector}. Modifiez-les pour les adapter à cette entreprise.`
                        : 'Coefficients par défaut. Modifiez-les pour les adapter à cette entreprise.'}
                    </Banner>
                  ) : null}
                  {attestationLabel ? (
                    <div className={'text-gray-500 text-sm'}>
                      {attestationLabel}
                    </div>
                  ) : null}
                  {/* Bounded so the table's own wrapper is the scroller: its
                      `h-full` resolves to nothing in an unbounded parent, the
                      panel body scrolls instead, and the sticky header rides
                      away with it. */}
                  <div className={'flex-1 min-h-0'}>
                    <Table
                      champsBody={coeffRows}
                      setChampsBody={markCoeffTouched}
                      errors={coeffErrors}
                      champsHeader={titlesCoeff}
                      type={'coeff'}
                    />
                  </div>
                </div>
              </SlideOverBody>
              <SlideOverFooter>
                <button
                  type={'button'}
                  className={'underline text-sm mr-auto'}
                  onClick={restoreDefaults}
                >
                  {defaultCoefficientsSector
                    ? `Valeurs par défaut du secteur ${defaultCoefficientsSector}`
                    : 'Valeurs par défaut'}
                </button>
                {isCoeffSaving ? (
                  <>
                    <Button size={'SM'} color={'SECONDARY'}>
                      Annuler
                    </Button>
                    <Button size={'SM'} color={'DISABLED'}>
                      Confirmation ...
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size={'SM'}
                      color={'SECONDARY'}
                      onClick={discardCoeffDraft}
                    >
                      Annuler
                    </Button>
                    <Button
                      size={'SM'}
                      color={canSubmitCoeff ? 'PRIMARY' : 'DISABLED'}
                      onClick={submitCoeff}
                    >
                      Confirmer
                    </Button>
                  </>
                )}
              </SlideOverFooter>
            </>
          )}
        </>
      )}
    </>
  );
};

export default SlideOverCompany;
